import type { SubscriptionResponse } from '@kimuno/api-sdk';
import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';

import { isRenewedSubscription, isTrialSubscription } from './subscription';

// "First 14 days after a new subscription is submitted (day 14 included)" —
// the conclusion day counts as day 1, so the diff in calendar days must be 0..13.
const WITHDRAWAL_WINDOW_DAYS = 14;

const isWithinWindowDays = (startDateIso: string | undefined, now: Date): boolean => {
	if (!startDateIso) return false;
	const startDate = parseISO(startDateIso);
	if (!isValid(startDate)) return false;
	const diff = differenceInCalendarDays(now, startDate);
	return diff >= 0 && diff < WITHDRAWAL_WINDOW_DAYS;
};

/**
 * Whether the user may withdraw (Widerruf) their contract right now.
 *
 * SM-117: this is the LEGACY Zuora path only, used by ManageSubscriptionDialog.
 * For Stripe-backed subscriptions, kimuno owns withdrawal eligibility (the
 * `revocation` block on the subscription-details response) and the caller gates
 * on `revocation.revocable` instead — see SubscriptionInfo (SM-254), which
 * surfaces the Widerruf button + StripeWithdrawalDialog for Stripe users.
 *
 * Withdrawal applies ONLY to a freshly concluded online Premium contract (SEAR-562):
 *
 * - Trial-origin subscriptions are excluded (see `isTrialSubscription`: in-trial, trial
 *   licence, or school trial). The contract was concluded at trial sign-up, and converting to
 *   Premium does not open a new withdrawal window — trial users use the regular (trial)
 *   cancellation flow instead. [#1]
 * - Renewed subscriptions are excluded. A renewal is not a new contract conclusion. On
 *   renewal `startDate` moves forward to the new term while `originalCreatedDate` stays
 *   fixed, so they differ once the subscription has renewed (see ampli.ts). [#2]
 */
export const isWithinWithdrawalWindow = (
	subscription: SubscriptionResponse | undefined,
	now: Date = new Date()
): boolean => {
	if (!subscription) return false;
	if (isTrialSubscription(subscription)) return false;
	if (isRenewedSubscription(subscription)) return false;
	return isWithinWindowDays(subscription.originalCreatedDate, now);
};
